import { AppShell, IconHeart, SavedTemplate } from '@fontbold/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { AppTabBar, AppTopBar } from '#/components/AppChrome';
import { AppNewsletter, openNewsletter } from '#/components/AppNewsletter';
import { useCookieConsent } from '#/lib/consent';
import { createLocationLabels } from '#/lib/location';
import { locationSummaryQueries } from '#/lib/location-summary';
import { useSaved } from '#/lib/saved';
import { savedPlacesQueries } from '#/lib/saved-places';
import { pageTitle } from '#/lib/site';
import { getSpace } from '#/lib/space-fn';
import { spaceLogo } from '#/lib/space-logo';
import { SAVED_TRANSLATIONS } from '#/lib/translations';

export const Route = createFileRoute('/bewaard')({
  loader: async ({ context }) => {
    const space = await getSpace();
    if (!space) return { space: null, cities: [] };

    const cities = await context.queryClient.ensureQueryData(
      locationSummaryQueries.cities(space.id),
    );

    return { space, cities };
  },
  // After loader: TypeScript infers these options in order, so head must
  // follow the loader to see its data type.
  head: ({ loaderData }) => ({
    meta: [{ title: pageTitle('Bewaard', loaderData?.space) }],
  }),
  component: SavedPage,
});

function SavedPage() {
  const { space, cities } = Route.useLoaderData();
  const { ids } = useSaved();
  const { functional } = useCookieConsent();

  const { data: places = [] } = useQuery({
    ...savedPlacesQueries.byIds(
      space?.id ?? '',
      ids,
      createLocationLabels(cities),
    ),
    enabled: Boolean(space) && functional && ids.length > 0,
  });

  if (!space) return null;

  const logo = spaceLogo(space);

  if (!functional) {
    return (
      <>
        <AppShell
          topBar={
            <AppTopBar
              space={space}
              active="bewaard"
              onNewsletter={openNewsletter}
            />
          }
          tabBar={<AppTabBar space={space} active="bewaard" />}
        >
          <div className="mx-auto flex max-w-[38ch] flex-col items-center gap-3.5 px-5 py-24 text-center">
            <span className="grid h-16 w-16 place-items-center rounded-[var(--radius-pill)] bg-[var(--surface-elevated)]">
              <IconHeart size={27} color="var(--content-faint)" />
            </span>
            <h1
              className="m-0 text-[18px] font-extrabold tracking-[-0.025em] md:text-[22px]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Bewaren staat uit
            </h1>
            <p className="m-0 text-[14px] leading-[1.55] text-[var(--content-muted)] md:text-[15px]">
              Je hebt functionele cookies uitgezet. Daardoor kunnen we niet
              onthouden welke plekken je bewaart.
            </p>
            <a
              href="/cookie-instellingen"
              className="mt-1.5 flex h-11 items-center rounded-[var(--radius-sm)] bg-[var(--surface-inverted)] px-5 text-[14px] font-bold text-[var(--content-inverted)]"
            >
              Cookie-instellingen aanpassen
            </a>
          </div>
        </AppShell>
        <AppNewsletter spaceId={space.id} logo={logo} />
      </>
    );
  }

  return (
    <>
      <SavedTemplate
        topBar={
          <AppTopBar
            space={space}
            active="bewaard"
            onNewsletter={openNewsletter}
          />
        }
        tabBar={<AppTabBar space={space} active="bewaard" />}
        translations={SAVED_TRANSLATIONS}
        emptyCtaHref="/"
        places={places}
      />
      <AppNewsletter spaceId={space.id} logo={logo} />
    </>
  );
}
